import { DWB_LESSONS_BASE_PATH } from 'containers/Product/constants'
import { getProductNameFromUrl } from 'utils/routing/routeHelpers'
import { BACK_LINK_LABEL_BY_PRODUCT } from './constants'

export function getBackLinkLabel({ contentGroupCode, productName } = {}) {
  const resolvedProductName =
    contentGroupCode === 'dwb' ? 'digital-well-being' : productName

  return BACK_LINK_LABEL_BY_PRODUCT[resolvedProductName] ?? 'Lessons'
}

function resolveLessonsLandingPath(productName) {
  return productName ? `/product/${productName}/lessons` : DWB_LESSONS_BASE_PATH
}

const LESSON_VERB_PATTERN = /\/lessons\/(teach|preview)\//

/**
 * Tab summaries from curriculum-service may always point at preview routes.
 * Preserve the user's current teach/preview context when switching units.
 */
export const applyPresentationVerbToRoute = (route, presentationVerb) => {
  if (!route || typeof route !== 'string') return route

  const verb = String(presentationVerb || '').toLowerCase()
  if (verb !== 'teach' && verb !== 'preview') return route
  if (!LESSON_VERB_PATTERN.test(route)) return route

  return route.replace(LESSON_VERB_PATTERN, `/lessons/${verb}/`)
}

/**
 * Creates a back-click handler for UnitV3. Routes to the product lessons
 * landing, preserving query params when present.
 */
export const handleBackClick = ({ location, navigationEvent, productName }) => {
  return function onBackClick() {
    const search = location?.search || ''
    const searchSuffix = search
      ? search.startsWith('?')
        ? search
        : `?${search}`
      : ''
    const destination = resolveLessonsLandingPath(productName)

    navigationEvent(`${destination}${searchSuffix}`)
  }
}

/**
 * Factory for the unit-tab change handler. Mirrors TieredInterventionsUnits
 * so that downstream code can swap the page in without rewiring callers.
 */
export const handleTabChange = ({
  location,
  navigationEvent,
  presentationVerb,
}) => {
  return function onTabChange(tab) {
    if (!tab?.route) return

    const { search } = location || {}
    const query = new URLSearchParams(search).toString()
    const route = applyPresentationVerbToRoute(tab.route, presentationVerb)
    const destination = query ? `${route}?${query}` : route
    navigationEvent(destination)
  }
}

/**
 * UnitTabSet passes a unit slug string, while handleTabChange expects a tab
 * summary object with a `route`. This adapter resolves the slug to the tab
 * route before navigating.
 */
export const createUnitTabSelectHandler = ({
  location,
  navigationEvent,
  unitTabs = [],
  match,
}) => {
  const presentationVerb = match?.params?.verb
  const onTabChange = handleTabChange({
    location,
    navigationEvent,
    presentationVerb,
  })

  return unitSlugOrTab => {
    if (unitSlugOrTab?.route) {
      onTabChange(unitSlugOrTab)
      return
    }

    if (typeof unitSlugOrTab !== 'string' || !unitSlugOrTab) return

    const matchedTab = unitTabs.find(
      tab => tab.route?.split('/').pop() === unitSlugOrTab,
    )
    if (matchedTab) {
      onTabChange(matchedTab)
      return
    }

    const currentUnit = match?.params?.unit
    const pathname = location?.pathname || match?.url || ''
    const search = location?.search || ''

    if (currentUnit && pathname.endsWith(`/${currentUnit}`)) {
      navigationEvent(
        `${pathname.slice(
          0,
          -(currentUnit.length + 1),
        )}/${unitSlugOrTab}${search}`,
      )
    }
  }
}

export const getPathForRoute = ({
  routeValue,
  matchUrl,
  queryParamsString,
}) => {
  if (!routeValue || typeof routeValue !== 'string') return ''
  if (routeValue.startsWith('/')) return `${routeValue}${queryParamsString}`
  return `${matchUrl}/${routeValue}${queryParamsString}`
}

export const getTrainingPathForRoute = ({
  routeValue,
  matchUrl,
  productName,
  queryParamsString,
}) => {
  if (!routeValue || typeof routeValue !== 'string') return ''
  const resolvedProductName = productName || getProductNameFromUrl(matchUrl)

  if (routeValue.startsWith('/product/')) {
    return `${routeValue}${queryParamsString}`
  }

  if (routeValue.startsWith('/training/')) {
    if (!resolvedProductName) {
      return `${routeValue}${queryParamsString}`
    }
    return `/product/${resolvedProductName}${routeValue}${queryParamsString}`
  }

  if (routeValue.startsWith('/')) return `${routeValue}${queryParamsString}`

  if (!resolvedProductName) {
    return getPathForRoute({ routeValue, matchUrl, queryParamsString })
  }

  return `/product/${resolvedProductName}/training/${routeValue}${queryParamsString}`
}
